import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { fmtDate } from "@/components/erp/ErpUi";
import { PageHead, Stat, Empty, RangePicker, monthStartIso, todayIso } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ClipboardList, ShoppingCart } from "lucide-react";

export default function OwnerLowMargin() {
  const [range, setRange] = useState([monthStartIso(), todayIso()]);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/erp/low-margin", { params: { from: range[0], to: range[1] } })
    .then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))), [range]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={AlertTriangle} kicker="Alerta" title="Margem baixa">
        <RangePicker from={range[0]} to={range[1]} onChange={setRange} testid="low-range" />
      </PageHead>
      <p className="text-xs text-zinc-500 max-w-2xl">OS e vendas aprovadas com margem abaixo do mínimo que você definiu em Configurações › Financeiro. Padrão: 30%.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat testid="low-min" label="Margem mínima" value={data ? `${data.min_margin_pct}%` : "…"} />
        <Stat testid="low-count" label="OS/vendas afetadas" value={data?.count ?? "…"} tone={data?.count > 0 ? "red" : "green"} />
        <Stat testid="low-worst" label="Pior margem" value={data?.items?.[0] ? `${data.items[0].margin.toFixed(1)}%` : "—"} tone={data?.items?.[0]?.margin < 10 ? "red" : "amber"} />
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" />
        : data.items.length === 0 ? <Empty>Sem OS ou vendas abaixo da margem no período. Tudo saudável!</Empty>
        : (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead><tr className="bg-zinc-900 text-[11px] font-mono-caps text-zinc-500 uppercase">
                <th className="text-left px-3 py-2">Tipo</th><th className="text-left px-3 py-2">Número</th>
                <th className="text-left px-3 py-2">Cliente</th><th className="text-right px-3 py-2">Receita</th>
                <th className="text-right px-3 py-2">Custo</th><th className="text-right px-3 py-2">Lucro</th>
                <th className="text-right px-3 py-2">Margem</th><th className="text-left px-3 py-2">Data</th>
              </tr></thead>
              <tbody>{data.items.map((r) => (
                <tr key={`${r.type}-${r.id}`} data-testid={`low-row-${r.id}`} className="border-t border-zinc-800">
                  <td className="px-3 py-2 text-zinc-400">{r.type === "os" ? <ClipboardList className="inline h-4 w-4 mr-1" /> : <ShoppingCart className="inline h-4 w-4 mr-1" />}{r.type.toUpperCase()}</td>
                  <td className="px-3 py-2">
                    <Link to={r.type === "os" ? `/owner/ordens/${r.id}` : `/owner/balcao/${r.id}`} className="text-green-neon hover:underline">#{r.order_number}</Link>
                  </td>
                  <td className="px-3 py-2 text-zinc-100">{r.customer_name || "—"}{r.plate ? <span className="ml-2 text-[11px] text-zinc-500">{r.plate}</span> : null}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{brl(r.revenue)}</td>
                  <td className="px-3 py-2 text-right text-red-300 tabular-nums">{brl(r.cost)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.profit < 0 ? "text-red-300" : "text-zinc-200"}`}>{brl(r.profit)}</td>
                  <td className={`px-3 py-2 text-right font-bold tabular-nums ${r.margin < 10 ? "text-red-300" : "text-amber-brand"}`}>{r.margin.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-zinc-400">{fmtDate((r.created_at || "").slice(0, 10))}</td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
    </div>
  );
}
